if (!customElements.get('product-inventory')) {
  class ProductInventory extends HTMLElement {
    connectedCallback() {
      this.initLazySection();
    }

    disconnectedCallback() {
      this.variantPicker?.removeEventListener('on:variant:change', this.changeHandler);
    }

    initLazySection() {
      this.inventoryNotice = this.querySelector('.js-inventory-notice');
      this.urgencyMessage = this.querySelector('.js-inventory-urgency');
      this.indicatorBar = this.querySelector('.js-inventory-indicator');
      this.variantInventory = this.getVariantInventory();

      // Bind to on:variant:change event for this product
      const variantPicker = this.closest('.product-info').querySelector('variant-picker');
      this.variantPicker = variantPicker;
      this.changeHandler = this.handleVariantChange.bind(this);
      if (variantPicker) {
        variantPicker.addEventListener('on:variant:change', this.changeHandler);
        if ('variant' in variantPicker) {
          this.handleVariantChange({ detail: { variant: variantPicker.variant } });
          return;
        }
      }

      // Init
      this.updateInventory(
        parseInt(this.dataset.inventoryQuantity, 10),
        this.dataset.variantAvailable === 'true',
        this.dataset.inventoryPolicy,
        this.dataset.inventoryManaged === 'true'
      );
    }

    /**
     * Gets the inventory data for all product variants
     * @returns {?object}
     */
    getVariantInventory() {
      const dataEl = this.querySelector('[type="application/json"]');
      return this.variantInventory || JSON.parse(dataEl.textContent);
    }

    /**
     * Handles a 'change' event on the variant picker element
     * @param {object} evt - Event object
     */
    handleVariantChange(evt) {
      if (evt.detail.variant) {
        const inventory = this.variantInventory.filter(
          (variant) => variant.id === evt.detail.variant.id
        )[0];
        if (!inventory) { this.clearNotice(); return; }
        this.updateInventory(
          inventory.inventory_quantity,
          inventory.available,
          inventory.inventory_policy,
          !!inventory.inventory_management
        );
      } else {
        this.clearNotice();
      }
    }

    /**
     *
     * Updates the inventory notice
     * @param {number} count - the inventory quantity available
     * @param {boolean} available - whether current variant is available
     * @param {string} inventoryPolicy - whether product continues selling when out of stock
     */
    clearNotice() {
      this.hidden = true;
      this.inventoryNotice.textContent = '';
      if (this.urgencyMessage) this.urgencyMessage.textContent = '';
      this.removeAttribute('data-inventory-level');
    }

    updateInventory(count, available, inventoryPolicy, managed = true) {
      let inventoryLevel;

      if (!available) {
        inventoryLevel = 'none';
      } else if (!managed) {
        inventoryLevel = 'in_stock';
      } else if (count <= 0) {
        if (inventoryPolicy === 'continue') {
          inventoryLevel = 'backordered';
        } else if (available) {
          inventoryLevel = 'in_stock';
        } else {
          inventoryLevel = 'none';
        }
      } else if (count <= parseInt(this.dataset.thresholdVeryLow, 10)) {
        inventoryLevel = 'very_low';
      } else if (count <= parseInt(this.dataset.thresholdLow, 10)) {
        inventoryLevel = 'low';
      } else {
        inventoryLevel = 'normal';
      }

      const displayLevel = ['none', 'backordered'].includes(inventoryLevel) ? inventoryLevel : 'in_stock';

      if (((this.dataset.showNotice === 'always'
        || (this.dataset.showNotice === 'low' && inventoryLevel.includes('low')))
        && !(inventoryLevel === 'backordered' && this.dataset.showNoStockBackordered === 'false'))) {
        this.hidden = false;

        // Set the inventory level data attribute (drives the status dot color)
        this.setAttribute('data-inventory-level', displayLevel);

        // Keep backorders distinct from physical stock.
        // The out-of-stock label uses a product-page-only string (data-status-no-stock,
        // default "Revine curând") so it stays softer here without affecting the
        // shared "Stoc Epuizat" wording used on product cards.
        if (displayLevel === 'none') {
          this.inventoryNotice.innerText = this.dataset.statusNoStock || theme.strings.outOfStock;
        } else if (displayLevel === 'backordered') {
          this.inventoryNotice.innerText = this.dataset.statusBackorder;
        } else {
          this.inventoryNotice.innerText = theme.strings.inStock;
        }

        // Update the matching urgency message.
        if (this.urgencyMessage) {
          if (displayLevel === 'none') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStock;
          } else if (displayLevel === 'backordered') {
            this.urgencyMessage.innerHTML = this.dataset.textNoStockBackordered;
          } else {
            this.urgencyMessage.innerHTML = this.dataset.textNormal;
          }

        }

        // Update the bar indicator
        if (this.indicatorBar) {
          this.indicatorBar.hidden = false;

          let newWidth;
          if ((count >= this.dataset.scale || inventoryLevel === 'in_stock') && inventoryLevel !== 'backordered') {
            newWidth = 100;
          } else {
            newWidth = ((100 / parseInt(this.dataset.scale, 10)) * count).toFixed(1);
          }
          this.indicatorBar.querySelector('span').style.width = `${Math.max(0, Math.min(100, newWidth))}%`;
        }
      } else {
        this.clearNotice();
      }
    }
  }

  customElements.define('product-inventory', ProductInventory);
}
